import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Tv, Play, Pause, RotateCcw, FlipHorizontal, Camera, CameraOff,
  Smartphone, Cloud, Download, Save, Crown, FileText, Plus, AlertTriangle,
  Mic, MicOff, Square, Film, X,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  listLocalScripts, saveLocalScript, deleteLocalScript,
  cacheCloudScripts, getCachedCloudScripts, downloadScriptAsTxt,
} from "../lib/teleprompterStorage";

// Speed: stored internally as px/sec, displayed as 1.0×–10.0× (1× = 24 px/s baseline).
// 24 px/s @ 48px font ≈ a comfortable reading pace; 10× is the upper bound for fast skim.
const SPEED_STEP_PX = 24;          // 1× = 24 px/s
const SPEED_MIN = SPEED_STEP_PX;   // 24 px/s  → 1.0×
const SPEED_MAX = SPEED_STEP_PX * 10; // 240 px/s → 10.0×
const SPEED_SLIDER_STEP = SPEED_STEP_PX / 2; // 0.5× increments
const speedToMultiplier = (px) => (px / SPEED_STEP_PX).toFixed(1) + "×";
const FONT_MIN = 24, FONT_MAX = 96;
const SPACING_MIN = 1.1, SPACING_MAX = 3.0;

// ---------- Upgrade Gate ----------
function UpgradeGate() {
  return (
    <div className="p-6 sm:p-8 lg:p-12 max-w-3xl mx-auto" data-testid="teleprompter-upgrade-gate">
      <div className="nb-card p-8 sm:p-10 bg-yellow-50 text-center">
        <div className="w-16 h-16 mx-auto bg-yellow-300 border-2 border-slate-900 rounded-md grid place-items-center" style={{ boxShadow: "4px 4px 0 0 #0F172A" }}>
          <Tv className="w-8 h-8" strokeWidth={2.5}/>
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-black mt-6">Upgrade to Studict Pro Plus to unlock the Teleprompter</h1>
        <p className="text-base font-medium text-slate-700 mt-4 max-w-xl mx-auto">
          Record yourself reading at the perfect pace with an auto-scrolling script, live full-screen camera, voice-activated start/stop, mirror mode for teleprompter glass, and adjustable speed, font and spacing.
        </p>
        <Link to="/pricing" data-testid="teleprompter-upgrade-cta" className="nb-btn-primary inline-flex items-center gap-2 mt-8">
          <Crown className="w-4 h-4" strokeWidth={2.5}/> See Pro Plus pricing
        </Link>
      </div>
    </div>
  );
}

// ---------- Tiny UI helpers ----------
function Slider({ label, value, display, min, max, step, onChange, suffix, testid }) {
  const shown = display ?? `${value}${suffix || ""}`;
  return (
    <label className="block text-xs font-bold uppercase tracking-widest text-slate-600">
      <span className="flex justify-between items-center">
        <span>{label}</span>
        <span className="text-slate-900 font-display text-base normal-case tracking-normal">{shown}</span>
      </span>
      <input type="range" min={min} max={max} step={step || 1} value={value} onChange={(e) => onChange(Number(e.target.value))} data-testid={testid} className="w-full mt-1 accent-yellow-500"/>
    </label>
  );
}

function ScriptListItem({ script, active, onLoad, onDelete, onDownload }) {
  const isLocal = script.source === "local" || (script.id || "").startsWith("local-");
  const dt = (script.updated_at || script.created_at || "").slice(0, 10);
  return (
    <li className={`nb-card-sm p-3 flex items-start gap-3 ${active ? "bg-yellow-200" : "bg-white"}`}>
      <div className="mt-0.5">{isLocal ? <Smartphone className="w-4 h-4" strokeWidth={2.5}/> : <Cloud className="w-4 h-4" strokeWidth={2.5}/>}</div>
      <div className="flex-1 min-w-0">
        <button onClick={onLoad} data-testid={`teleprompter-load-${script.id}`} className="block text-left w-full">
          <div className="font-bold text-sm truncate">{script.title || "Untitled script"}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">{isLocal ? "On this device" : "Cloud"} · {dt} · {Math.max(1, Math.round((script.length_bytes || 0) / 1024))} KB</div>
        </button>
        <div className="flex gap-1.5 mt-2">
          <button onClick={onDownload} data-testid={`teleprompter-download-${script.id}`} className="text-[11px] font-bold underline hover:no-underline">Download</button>
          <span className="text-slate-300">·</span>
          <button onClick={onDelete} data-testid={`teleprompter-delete-${script.id}`} className="text-[11px] font-bold text-red-600 underline hover:no-underline">Delete</button>
        </div>
      </div>
    </li>
  );
}

// ---------- Full-screen Recording Overlay ----------
function RecorderOverlay({
  stream, content, fontSize, lineSpacing, mirror, speed, voiceControl,
  onClose, onFinish,
}) {
  const [phase, setPhase] = useState("countdown"); // "countdown" | "running"
  const [countdown, setCountdown] = useState(3);
  const [playing, setPlaying] = useState(true);
  const [offset, setOffset] = useState(0);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [recording, setRecording] = useState(false);
  const [vadActive, setVadActive] = useState(false);

  const videoRef = useRef(null);
  const textRef = useRef(null);
  const rafRef = useRef(null);
  const lastTickRef = useRef(0);

  // MediaRecorder
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const recordedUrlRef = useRef(null);

  // VAD (voice activity detection) refs
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const vadRafRef = useRef(null);
  const silenceFramesRef = useRef(0);

  // 1) Mount: attach the stream that the parent already acquired (in the user
  //    gesture context). Start MediaRecorder + VAD here.
  useEffect(() => {
    if (!stream) return;
    let mounted = true;
    (async () => {
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try { await videoRef.current.play(); } catch { /* autoplay blocked — playsInline retries */ }
      }
      if (!mounted) return;

      // Start MediaRecorder if the stream has an audio track (or video-only) and the API exists
      if (typeof MediaRecorder !== "undefined") {
        try {
          const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
            ? "video/webm;codecs=vp9,opus"
            : (MediaRecorder.isTypeSupported("video/webm") ? "video/webm" : "");
          const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
          chunksRef.current = [];
          rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
          rec.onstop = () => {
            const blob = new Blob(chunksRef.current, { type: rec.mimeType || "video/webm" });
            const url = URL.createObjectURL(blob);
            recordedUrlRef.current = url;
            setRecordedUrl(url);
          };
          rec.start();
          recorderRef.current = rec;
          setRecording(true);
        } catch (e) {
          // MediaRecorder unsupported on this browser/codec — preview still works
          console.warn("MediaRecorder not supported:", e);
        }
      }

      // Voice-activity detection (needs an audio track)
      const hasAudio = stream.getAudioTracks().length > 0;
      if (voiceControl && hasAudio) {
        try {
          const AC = window.AudioContext || window.webkitAudioContext;
          const ctx = new AC();
          const src = ctx.createMediaStreamSource(stream);
          const analyser = ctx.createAnalyser();
          analyser.fftSize = 512;
          src.connect(analyser);
          audioCtxRef.current = ctx;
          analyserRef.current = analyser;
          setVadActive(true);
          const buf = new Uint8Array(analyser.frequencyBinCount);
          const VAD_THRESHOLD = 18;
          const SILENCE_FRAMES = 30;
          const loop = () => {
            analyser.getByteFrequencyData(buf);
            let sum = 0;
            for (let i = 0; i < buf.length; i++) sum += buf[i];
            const avg = sum / buf.length;
            if (avg > VAD_THRESHOLD) {
              silenceFramesRef.current = 0;
              setPlaying(true);
            } else {
              silenceFramesRef.current += 1;
              if (silenceFramesRef.current > SILENCE_FRAMES) setPlaying(false);
            }
            vadRafRef.current = requestAnimationFrame(loop);
          };
          vadRafRef.current = requestAnimationFrame(loop);
        } catch (e) {
          console.warn("VAD init failed:", e);
        }
      }
    })();
    return () => {
      mounted = false;
      stopEverything();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream]);

  function stopEverything() {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (vadRafRef.current) cancelAnimationFrame(vadRafRef.current);
    rafRef.current = null;
    vadRafRef.current = null;
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try { recorderRef.current.stop(); } catch { /* ignore */ }
    }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch { /* ignore */ }
      audioCtxRef.current = null;
    }
    if (stream) {
      try { stream.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    }
    setRecording(false);
  }

  // 2) Countdown
  useEffect(() => {
    if (phase !== "countdown") return;
    if (countdown <= 0) { setPhase("running"); return; }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [phase, countdown]);

  // 3) Scrolling loop
  useEffect(() => {
    if (phase !== "running" || !playing) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      return;
    }
    lastTickRef.current = performance.now();
    const tick = (now) => {
      const dt = (now - lastTickRef.current) / 1000;
      lastTickRef.current = now;
      setOffset((prev) => prev + speed * dt);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [phase, playing, speed]);

  // 4) Auto-stop when text has fully scrolled past
  useEffect(() => {
    if (phase !== "running") return;
    const textH = textRef.current?.offsetHeight || 0;
    if (textH > 0 && offset > textH + window.innerHeight * 0.5) {
      setPlaying(false);
    }
  }, [offset, phase]);

  const handleStop = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try { recorderRef.current.stop(); } catch { /* ignore */ }
    }
    setTimeout(() => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (vadRafRef.current) cancelAnimationFrame(vadRafRef.current);
      rafRef.current = null;
      vadRafRef.current = null;
      if (audioCtxRef.current) {
        try { audioCtxRef.current.close(); } catch { /* ignore */ }
        audioCtxRef.current = null;
      }
      if (stream) {
        try { stream.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
      }
      setRecording(false);
      onFinish({ videoUrl: recordedUrlRef.current });
    }, 350);
  };

  // Force close (X button) without finishing
  const handleExit = () => {
    stopEverything();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black" data-testid="teleprompter-recorder-overlay">
      {/* Camera — full-viewport object-cover so face fills the screen on portrait and landscape.
          scaleX(-1) un-reverses the front-cam preview so the user's movements mirror naturally
          (turn left → on-screen image moves left, like a real mirror). */}
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover"
        style={{ transform: "scaleX(-1)" }}
        autoPlay
        muted
        playsInline
      />

      {/* Dim overlay for text legibility */}
      <div className="absolute inset-0 bg-black/40 pointer-events-none" aria-hidden="true" />

      {/* Countdown */}
      {phase === "countdown" && (
        <div className="absolute inset-0 grid place-items-center" data-testid="teleprompter-countdown">
          <div className="text-white text-center">
            <div className="font-display font-black leading-none" style={{ fontSize: "min(40vw, 280px)" }}>
              {countdown > 0 ? countdown : "GO!"}
            </div>
            <p className="text-white/80 mt-4 font-bold">Get ready…</p>
          </div>
        </div>
      )}

      {/* Scrolling text — first line is vertically centred on the cue line, then scrolls up */}
      {phase === "running" && (
        <>
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div
              className="absolute left-0 right-0 px-6 sm:px-12"
              style={{
                top: `calc(50% - ${(fontSize * lineSpacing) / 2}px - ${offset}px)`,
                transform: mirror ? "scaleX(-1)" : "none",
              }}
            >
              <div
                ref={textRef}
                style={{ fontSize: `${fontSize}px`, lineHeight: lineSpacing }}
                className="text-white font-bold text-center whitespace-pre-wrap drop-shadow-[0_2px_6px_rgba(0,0,0,0.95)]"
                data-testid="teleprompter-scrolling-text"
              >
                {content}
              </div>
            </div>
          </div>

          {/* Centre cue line */}
          <div className="absolute left-0 right-0 top-1/2 h-px bg-yellow-300/70 pointer-events-none" aria-hidden="true"/>
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] font-bold uppercase tracking-widest text-yellow-300/90 pointer-events-none">READ HERE</div>
        </>
      )}

      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 p-3 flex justify-between items-center pointer-events-none">
        <div className="pointer-events-auto flex items-center gap-2">
          {recording && (
            <span className="inline-flex items-center gap-2 px-2.5 py-1 bg-red-600 text-white text-xs font-bold rounded-full">
              <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> REC
            </span>
          )}
          {vadActive && (
            <span className="inline-flex items-center gap-2 px-2.5 py-1 bg-yellow-300 text-slate-900 text-xs font-bold rounded-full">
              <Mic className="w-3 h-3" strokeWidth={2.5}/> Voice
            </span>
          )}
        </div>
        <button onClick={handleExit} data-testid="teleprompter-recorder-exit" className="pointer-events-auto p-2 rounded-full bg-white/90 border-2 border-slate-900" aria-label="Exit without saving">
          <X className="w-5 h-5" strokeWidth={2.5}/>
        </button>
      </div>

      {/* Bottom controls */}
      {phase === "running" && (
        <div className="absolute bottom-6 inset-x-0 flex justify-center gap-3 pointer-events-none">
          <div className="pointer-events-auto flex gap-3">
            {!voiceControl && (
              <button
                onClick={() => setPlaying((p) => !p)}
                data-testid="teleprompter-recorder-playpause"
                className="px-5 py-3 bg-white border-2 border-slate-900 rounded-md font-bold inline-flex items-center gap-2"
                style={{ boxShadow: "3px 3px 0 0 #0F172A" }}
              >
                {playing ? <><Pause className="w-5 h-5" strokeWidth={2.5}/> Pause</> : <><Play className="w-5 h-5" strokeWidth={2.5}/> Play</>}
              </button>
            )}
            <button
              onClick={handleStop}
              data-testid="teleprompter-recorder-stop"
              className="px-6 py-3 bg-red-500 text-white border-2 border-slate-900 rounded-md font-bold inline-flex items-center gap-2"
              style={{ boxShadow: "3px 3px 0 0 #0F172A" }}
            >
              <Square className="w-5 h-5 fill-current" strokeWidth={2.5}/> Stop &amp; finish
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Main Page ----------
export default function Teleprompter() {
  const { user } = useAuth();
  const isProPlus = user?.plan === "pro_plus";

  // Editor state
  const [title, setTitle] = useState("Untitled script");
  const [content, setContent] = useState("");
  const [speed, setSpeed] = useState(60);
  const [fontSize, setFontSize] = useState(48);
  const [lineSpacing, setLineSpacing] = useState(1.6);
  const [mirror, setMirror] = useState(false);
  const [voiceControl, setVoiceControl] = useState(false);

  const [activeScript, setActiveScript] = useState(null);
  const [localList, setLocalList] = useState([]);
  const [cloudList, setCloudList] = useState([]);
  const [busySave, setBusySave] = useState(false);

  // Recording state
  const [recording, setRecording] = useState(false);
  const [lastVideoUrl, setLastVideoUrl] = useState(null);
  const [hasRecorded, setHasRecorded] = useState(false);

  const refreshLists = useCallback(async () => {
    if (!user) return;
    setLocalList(listLocalScripts(user.user_id));
    setCloudList(getCachedCloudScripts(user.user_id));
    try {
      const r = await api.get("/teleprompter/scripts");
      const arr = r.data?.scripts || [];
      cacheCloudScripts(user.user_id, arr);
      setCloudList(arr.map((s) => ({ ...s, source: "cloud" })));
    } catch { /* offline — cache already shown */ }
  }, [user]);

  useEffect(() => { if (isProPlus) refreshLists(); }, [isProPlus, refreshLists]);

  // Clean up object URL on unmount
  useEffect(() => () => {
    if (lastVideoUrl) URL.revokeObjectURL(lastVideoUrl);
  }, [lastVideoUrl]);

  const loadScript = (s) => {
    setActiveScript(s);
    setTitle(s.title || "Untitled script");
    setContent(s.content || "");
    setSpeed(Number(s.speed) || 60);
    setFontSize(Number(s.font_size) || 48);
    setLineSpacing(Number(s.line_spacing) || 1.6);
    setMirror(!!s.mirror);
    setHasRecorded(false);
    if (lastVideoUrl) { URL.revokeObjectURL(lastVideoUrl); setLastVideoUrl(null); }
    toast.success(`Loaded "${s.title || "Untitled"}"`);
  };

  const newScript = () => {
    setActiveScript(null);
    setTitle("Untitled script");
    setContent("");
    setHasRecorded(false);
    if (lastVideoUrl) { URL.revokeObjectURL(lastVideoUrl); setLastVideoUrl(null); }
  };

  // Active live MediaStream while recording (acquired in the user-gesture handler
  // so iOS Safari doesn't reject the permission prompt).
  const [stream, setStream] = useState(null);
  const [acquiring, setAcquiring] = useState(false);

  const startRecording = async () => {
    if (!content.trim()) { toast.error("Type or paste a script before starting."); return; }
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("This browser doesn't support camera access. Try Chrome or Safari.");
      return;
    }
    setAcquiring(true);
    let s = null;
    try {
      // Try video + audio (preferred — lets MediaRecorder save with sound and powers voice control)
      s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });
    } catch (e1) {
      const n1 = e1?.name || "";
      // If audio specifically failed (some Android browsers split permissions), retry video-only
      if (n1 !== "NotAllowedError" && n1 !== "SecurityError") {
        try {
          s = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
            audio: false,
          });
          toast.message("Microphone unavailable — recording video only.");
        } catch (e2) {
          showCamError(e2);
          setAcquiring(false);
          return;
        }
      } else {
        showCamError(e1);
        setAcquiring(false);
        return;
      }
    }
    setStream(s);
    setRecording(true);
    setAcquiring(false);
  };

  const showCamError = (err) => {
    const name = err?.name || "";
    if (name === "NotAllowedError" || name === "SecurityError") {
      toast.error("Camera permission denied. Open this page in your browser (not an in-app webview), then allow camera access in browser settings.", { duration: 10000 });
    } else if (name === "NotFoundError" || name === "OverconstrainedError") {
      toast.error("No camera found on this device.");
    } else if (name === "NotReadableError") {
      toast.error("Camera is in use by another app. Close other apps using the camera and try again.");
    } else {
      toast.error(`Couldn't access camera: ${err?.message || name || "unknown error"}`, { duration: 8000 });
    }
  };

  const finishRecording = ({ videoUrl }) => {
    setRecording(false);
    setStream(null);
    setHasRecorded(true);
    if (lastVideoUrl) URL.revokeObjectURL(lastVideoUrl);
    setLastVideoUrl(videoUrl || null);
    if (videoUrl) toast.success("Recording captured — download it below or save the script.");
    else toast.success("Practice run complete — you can now save the script.");
  };

  const cancelRecording = () => {
    if (stream) {
      try { stream.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    }
    setStream(null);
    setRecording(false);
  };

  const saveLocal = () => {
    if (!content.trim()) { toast.error("Type or paste a script first."); return; }
    const next = saveLocalScript(user.user_id, {
      id: activeScript && (activeScript.id || "").startsWith("local-") ? activeScript.id : null,
      title, content, speed, font_size: fontSize, line_spacing: lineSpacing, mirror,
    });
    setActiveScript(next);
    setLocalList(listLocalScripts(user.user_id));
    toast.success("Saved to this device — works offline.");
  };

  const saveToCloud = async () => {
    if (!content.trim()) { toast.error("Type or paste a script first."); return; }
    setBusySave(true);
    try {
      const payload = { title, content, speed, font_size: fontSize, line_spacing: lineSpacing, mirror };
      let saved;
      if (activeScript && activeScript.source === "cloud" && !(activeScript.id || "").startsWith("local-")) {
        const r = await api.put(`/teleprompter/scripts/${activeScript.id}`, payload);
        saved = r.data;
      } else {
        const r = await api.post("/teleprompter/scripts", payload);
        saved = r.data;
      }
      setActiveScript({ ...saved, source: "cloud" });
      await refreshLists();
      toast.success("Saved to your account.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save to your account.");
    } finally { setBusySave(false); }
  };

  const downloadVideo = () => {
    if (!lastVideoUrl) return;
    const a = document.createElement("a");
    a.href = lastVideoUrl;
    const safeTitle = (title || "teleprompter").replace(/[^a-z0-9-_ ]/gi, "_").slice(0, 60);
    a.download = `${safeTitle}.webm`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const onDeleteLocal = (id) => {
    deleteLocalScript(user.user_id, id);
    setLocalList(listLocalScripts(user.user_id));
    if (activeScript?.id === id) newScript();
    toast.success("Removed from device.");
  };
  const onDeleteCloud = async (id) => {
    try {
      await api.delete(`/teleprompter/scripts/${id}`);
      await refreshLists();
      if (activeScript?.id === id) newScript();
      toast.success("Deleted from your account.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete."); }
  };

  if (!isProPlus) return <UpgradeGate />;

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="teleprompter-page">
      <header className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Pro Plus</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black mt-1 flex items-center gap-2">
            <Tv className="w-7 h-7" strokeWidth={2.5}/> Teleprompter
          </h1>
        </div>
        <div className="flex gap-2">
          <button onClick={newScript} data-testid="teleprompter-new-btn" className="nb-btn-secondary inline-flex items-center gap-2">
            <Plus className="w-4 h-4" strokeWidth={2.5}/> New
          </button>
        </div>
      </header>

      <div className="grid lg:grid-cols-[280px_1fr] gap-5">
        {/* Saved scripts sidebar */}
        <aside className="space-y-3">
          <div className="nb-card p-4 bg-white">
            <h2 className="font-display text-sm font-black mb-2 flex items-center gap-1.5">
              <FileText className="w-4 h-4" strokeWidth={2.5}/> My scripts
            </h2>
            {localList.length === 0 && cloudList.length === 0 ? (
              <p className="text-xs text-slate-500">No scripts yet. Type one on the right, record yourself, then save.</p>
            ) : (
              <ul className="space-y-2" data-testid="teleprompter-script-list">
                {localList.map((s) => (
                  <ScriptListItem key={s.id} script={s} active={activeScript?.id === s.id}
                    onLoad={() => loadScript(s)} onDelete={() => onDeleteLocal(s.id)}
                    onDownload={() => { if (!downloadScriptAsTxt(s)) toast.error("Download failed."); }}/>
                ))}
                {cloudList.map((s) => (
                  <ScriptListItem key={s.id} script={s} active={activeScript?.id === s.id}
                    onLoad={() => loadScript(s)} onDelete={() => onDeleteCloud(s.id)}
                    onDownload={() => { if (!downloadScriptAsTxt(s)) toast.error("Download failed."); }}/>
                ))}
              </ul>
            )}
          </div>
        </aside>

        {/* Setup panel */}
        <section className="space-y-4">
          {/* Title + textarea */}
          <div className="nb-card p-4 bg-white">
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
              data-testid="teleprompter-title-input" placeholder="Script title"
              className="nb-input font-display text-lg font-bold"/>
            <textarea value={content} onChange={(e) => setContent(e.target.value)}
              data-testid="teleprompter-content-input" placeholder="Type or paste your script here…"
              rows={7} className="nb-input mt-3 font-medium leading-relaxed"/>
          </div>

          {/* Controls */}
          <div className="nb-card p-4 bg-white grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Slider label="Speed" value={speed} display={speedToMultiplier(speed)} min={SPEED_MIN} max={SPEED_MAX} step={SPEED_SLIDER_STEP} onChange={setSpeed} testid="teleprompter-speed-slider"/>
            <Slider label="Font size" value={fontSize} min={FONT_MIN} max={FONT_MAX} onChange={setFontSize} suffix="px" testid="teleprompter-font-slider"/>
            <Slider label="Line spacing" value={lineSpacing.toFixed(1)} min={SPACING_MIN} max={SPACING_MAX} step={0.1} onChange={setLineSpacing} testid="teleprompter-spacing-slider"/>
            <div className="space-y-2">
              <label className="block text-xs font-bold uppercase tracking-widest text-slate-600">
                <span>Mirror mode</span>
                <button onClick={() => setMirror((m) => !m)} data-testid="teleprompter-mirror-toggle"
                  className={`mt-1 w-full inline-flex items-center justify-center gap-2 py-2 rounded-md border-2 border-slate-900 font-bold text-sm transition-all ${mirror ? "bg-yellow-300" : "bg-white"}`}>
                  <FlipHorizontal className="w-4 h-4" strokeWidth={2.5}/>{mirror ? "On" : "Off"}
                </button>
              </label>
            </div>
          </div>

          {/* Voice-control toggle */}
          <div className="nb-card p-4 bg-white flex items-start gap-3">
            <button
              onClick={() => setVoiceControl((v) => !v)}
              data-testid="teleprompter-voice-control-toggle"
              className={`shrink-0 w-12 h-7 rounded-full border-2 border-slate-900 relative transition-colors ${voiceControl ? "bg-yellow-300" : "bg-slate-200"}`}
              aria-pressed={voiceControl}
            >
              <span className={`absolute top-0.5 ${voiceControl ? "left-[22px]" : "left-0.5"} w-5 h-5 rounded-full bg-white border-2 border-slate-900 transition-all`} />
            </button>
            <div className="flex-1">
              <p className="font-bold text-sm flex items-center gap-1.5">
                {voiceControl ? <Mic className="w-4 h-4" strokeWidth={2.5}/> : <MicOff className="w-4 h-4" strokeWidth={2.5}/>}
                Voice-controlled play/pause
              </p>
              <p className="text-xs text-slate-600 mt-0.5">
                When on, the script auto-scrolls while you're speaking and pauses when you stop. Needs microphone permission on first run.
              </p>
            </div>
          </div>

          {/* Start button */}
          <button
            onClick={startRecording}
            data-testid="teleprompter-start-btn"
            disabled={!content.trim() || acquiring}
            className="nb-btn-primary w-full inline-flex items-center justify-center gap-3 !py-4 text-base"
          >
            <Camera className="w-5 h-5" strokeWidth={2.5}/>
            {acquiring ? "Requesting camera…" : "Start — full-screen 3-second countdown"}
          </button>

          {/* Save panel — only after a recording */}
          {hasRecorded && (
            <div className="nb-card p-4 bg-emerald-50 space-y-3" data-testid="teleprompter-save-panel">
              <h3 className="font-display text-lg font-black flex items-center gap-2">
                <Film className="w-5 h-5" strokeWidth={2.5}/> Practice run complete
              </h3>
              {lastVideoUrl && (
                <div>
                  <video src={lastVideoUrl} controls className="w-full rounded-md border-2 border-slate-900" data-testid="teleprompter-last-video"/>
                  <button onClick={downloadVideo} data-testid="teleprompter-download-video-btn" className="nb-btn-secondary inline-flex items-center gap-2 mt-3">
                    <Download className="w-4 h-4" strokeWidth={2.5}/> Download video (.webm)
                  </button>
                </div>
              )}
              <div className="flex flex-wrap gap-2 pt-2 border-t-2 border-emerald-200">
                <button onClick={saveLocal} data-testid="teleprompter-save-local-btn" className="nb-btn-secondary inline-flex items-center gap-2">
                  <Smartphone className="w-4 h-4" strokeWidth={2.5}/> Save script to phone
                </button>
                <button onClick={saveToCloud} disabled={busySave} data-testid="teleprompter-save-cloud-btn" className="nb-btn-primary inline-flex items-center gap-2">
                  <Save className="w-4 h-4" strokeWidth={2.5}/> {busySave ? "Saving…" : "Save script to account"}
                </button>
                <button onClick={() => { if (!downloadScriptAsTxt({ title, content })) toast.error("Download failed."); }}
                  data-testid="teleprompter-download-current-btn" className="nb-btn-secondary inline-flex items-center gap-2">
                  <Download className="w-4 h-4" strokeWidth={2.5}/> Download script .txt
                </button>
              </div>
            </div>
          )}

          <div className="nb-card-sm p-3 bg-amber-50 flex items-start gap-2 text-xs">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={2.5}/>
            <span>
              Recordings stay on your device (browser memory) until you download them. They are never uploaded to Studict.
            </span>
          </div>
        </section>
      </div>

      {recording && stream && (
        <RecorderOverlay
          stream={stream}
          content={content}
          fontSize={fontSize}
          lineSpacing={lineSpacing}
          mirror={mirror}
          speed={speed}
          voiceControl={voiceControl}
          onClose={cancelRecording}
          onFinish={finishRecording}
        />
      )}
    </div>
  );
}
